export type KycV2Module = "BASIC" | "POI" | "POA" | "AML" | "LIVENESS";

export type KycV2ModuleStatus =
  | "pending"
  | "approved"
  | "needs_review"
  | "declined"
  | string;

export type KycV2OverallStatus =
  | "incomplete"
  | "pending_review"
  | "approved"
  | "rejected"
  | "retry"
  | string;

export interface FetchKycV2RecordsRequest {
  page: number;
  limit: number;
  userId?: number | string;
  email?: string;
  overallKycStatus?: string;
  basicStatus?: string;
  poiStatus?: string;
  poaStatus?: string;
  amlStatus?: string;
  livenessStatus?: string;
}

/** Flat list-row shape from GET /kycV2/records */
export interface KycV2ListRecord {
  userId: number;
  ownerId?: number | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  basicStatus?: KycV2ModuleStatus | null;
  poiStatus?: KycV2ModuleStatus | null;
  poaStatus?: KycV2ModuleStatus | null;
  amlStatus?: KycV2ModuleStatus | null;
  livenessStatus?: KycV2ModuleStatus | null;
  overallKycStatus?: KycV2OverallStatus | null;
  kycCompletedAt?: string | null;
  updatedAt?: string | null;
  createdAt?: string | null;
  poiDump?: any;
  poaDump?: any;
  amlDump?: any;
  [key: string]: any;
}

export interface KycV2BasicModuleData {
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
  userCountry?: string | null;
  countryOfBirth?: string | null;
  taxId?: string | null;
  [key: string]: any;
}

export interface KycV2ModuleInfo {
  status?: KycV2ModuleStatus | null;
  serviceProvider?: string | null;
  completedAt?: string | null;
  updatedAt?: string | null;
  data?: KycV2BasicModuleData | null;
  [key: string]: any;
}

/** Full DB row nested under detail response `raw` */
export interface KycV2RawRecord {
  id?: number | string;
  userId: number;
  ownerId?: number | null;
  email?: string | null;
  phone?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  userCountry?: string | null;
  countryOfBirth?: string | null;
  taxId?: string | null;
  dateOfBirth?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  stateProvinceRegion?: string | null;
  postalCode?: string | null;
  country?: string | null;

  basicStatus?: KycV2ModuleStatus | null;
  poiStatus?: KycV2ModuleStatus | null;
  poaStatus?: KycV2ModuleStatus | null;
  amlStatus?: KycV2ModuleStatus | null;
  livenessStatus?: KycV2ModuleStatus | null;
  overallKycStatus?: KycV2OverallStatus | null;

  poiType?: string | null;
  poiNumber?: string | null;
  poiServiceProvider?: string | null;
  poiDump?: any;
  poiImageUrl?: string | null;
  poiCompletedAt?: string | null;
  poiApprovedBy?: number | null;

  poaType?: string | null;
  poaNumber?: string | number | null;
  poaServiceProvider?: string | null;
  poaDump?: any;
  poaImageFrontUrl?: string | null;
  poaImageBackUrl?: string | null;
  poaCompletedAt?: string | null;
  poaApprovedBy?: number | null;

  amlServiceProvider?: string | null;
  amlDump?: any;
  amlUpdatedAt?: string | null;

  livenessServiceProvider?: string | null;
  livenessUpdatedAt?: string | null;
  selfieImageUrl?: string | null;
  faceMatchScore?: number | null;
  livenessScore?: number | null;
  sessionUrl?: string | null;

  kycCompletedAt?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  isManualKYC?: number | boolean | null;

  [key: string]: any;
}

/** Detail shape from GET /kycV2/record/:userId */
export interface KycV2DetailRecord {
  userId: number;
  overallKycStatus?: KycV2OverallStatus | null;
  kycCompletedAt?: string | null;
  walletOpsAllowed?: boolean;
  modules: Partial<Record<KycV2Module, KycV2ModuleInfo>>;
  raw?: KycV2RawRecord | null;
}

export interface FetchKycV2RecordsResponse {
  msg: string;
  data: {
    records: KycV2ListRecord[];
    page?: number;
    limit?: number;
    total?: number;
    [key: string]: any;
  };
}

export interface FetchKycV2RecordResponse {
  msg: string;
  data: KycV2DetailRecord;
}

export interface KycV2ModuleActionRequest {
  userId: number | string;
  module: KycV2Module;
  reason?: string;
}

export interface KycV2ModuleActionResponse {
  msg: string;
  data?: KycV2DetailRecord | null;
}

export type DiditKycModule = "poi" | "poa" | "aml" | "liveness";

export interface DiditKycModuleState {
  status?: string | null;
  isRequired?: boolean;
  sessionId?: string | null;
}

export interface DiditKycDetails {
  modules?: Partial<Record<DiditKycModule, DiditKycModuleState>> | null;
}

export interface UpdateDiditFeatureStatusRequest {
  userId: number;
  module: DiditKycModule;
  reason?: string;
}

export interface UpdateDiditFeatureStatusResponse {
  msg: string;
  data?: unknown;
  error?: string;
}

export const DIDIT_KYC_MODULES: { key: DiditKycModule; label: string }[] = [
  { key: "poi", label: "POI" },
  { key: "poa", label: "POA" },
  { key: "aml", label: "AML" },
  { key: "liveness", label: "Liveness" },
];

export const KYC_V2_MODULES: { key: KycV2Module; label: string }[] = [
  { key: "BASIC", label: "Basic" },
  { key: "POI", label: "POI (ID)" },
  { key: "POA", label: "POA (Address)" },
  { key: "AML", label: "AML" },
  { key: "LIVENESS", label: "Liveness" },
];

export const getKycV2OverallStatus = (
  record: { overallKycStatus?: string | null; overallStatus?: string | null } | null | undefined,
): string => {
  if (!record) return "";
  return String(record.overallKycStatus || record.overallStatus || "");
};

export const getKycV2ModuleStatusFromDetail = (
  detail: KycV2DetailRecord | null | undefined,
  module: KycV2Module,
): string => {
  if (!detail?.modules) return "";
  const status = detail.modules[module]?.status;
  return status != null ? String(status) : "";
};
